import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  SnackbarProps,
  Chip,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party

import { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";

// project-import

import IconButton from "../../../../../components/@extended/IconButton";
import { FormattedMessage } from "react-intl";

// assets
import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  TranslationOutlined,
} from "@ant-design/icons";

import { openSnackbar } from "../../../../../api/snackbar";

import Loader from "../../../../../components/Loader";

import FiltersModal from "./FiltersModal";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import ReusableActivateDialog from "../../../../../components/ReusableComponent/ReusableActivateDialog";
import { Vacancy } from "../../../../../types/jobs/vacancy";
import { VacancyFiltersType } from "./FiltersModal";
import useConfig from "hooks/useConfig";

// assets
import { ThemeDirection } from "types/config";

const VacanciesList = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [openFilters, setOpenFilters] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [vacancyDeleteId, setVacancyDeleteId] = useState<any>("");
  const [result, setResult] = useState<Vacancy[]>([]);
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [activateTarget, setActivateTarget] = useState<Vacancy | null>(null);
  const { i18n, onChangeLocalization, onChangeDirection } = useConfig();

  const handleClose = () => {
    setOpen(!open);
  };

  const handleSearch = () => {
    setLoadingSearch(true);
    setCurrenttPage(1);
    setSearchText(tempSearchText);
    setLoadingSearch(false);
  };

  const columns = useMemo<ColumnDef<Vacancy>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="subtitle2">{row.original.id}</Typography>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="User Name" />,
        accessorKey: "company.user_name",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.company.user_name}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Email" />,
        accessorKey: "company.email",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.company.email}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Title" />,
        accessorKey: "title",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.title}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Country" />,
        accessorKey: "country.name",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.country.name}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Applicants" />,
        accessorKey: "applicants_count",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.applicants_count}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Views" />,
        accessorKey: "views",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.views}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Created At" />,
        accessorKey: "created_at",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.created_at.split("T")[0]}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        meta: {
          className: "cell-center",
        },
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const isActive = status === "ACTIVE";
          return (
            <Chip
              color={isActive ? "success" : "error"}
              label={<FormattedMessage id={isActive ? "Active" : "Inactive"} />}
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => {
          const { id } = row.original;

          const handleEditVacancy = () => {
            window.location.href = `/vacancies/edit-vacancy/${id}`;
          };

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip
                title={
                  row.original.status === "ACTIVE" ? "Deactivate" : "Activate"
                }
              >
                <IconButton
                  color={row.original.status === "ACTIVE" ? "success" : "error"}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setActivateTarget(row.original);
                    setActivateModal(true);
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  <CheckCircleOutlined
                    style={{
                      color:
                        row.original.status === "ACTIVE" ? "error" : "success",
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={handleEditVacancy}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setVacancyDeleteId(id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    []
  );

  const fetchData = () => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);

    const id = urlParams.get("id");
    const company_name = urlParams.get("company_name");
    const country_id = urlParams.get("country_id");
    const city_id = urlParams.get("city_id");
    const job_activity_id = urlParams.get("job_activity_id");

    USERAXIOS.get(
      `/vacancies?page=${currentPage}` +
        (searchText ? `&title=${searchText}` : "") +
        (id ? `&id=${id}` : "") +
        (company_name ? `&company_name=${company_name}` : "") +
        (country_id ? `&country_id=${country_id}` : "") +
        (city_id ? `&city_id=${city_id}` : "") +
        (job_activity_id ? `&job_activity_id=${job_activity_id}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching vacancy profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching vacancy profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleFiltersClose = () => {
    setOpenFilters(false);
  };

  const clearFilters = () => {
    setLoading(true);
    window.history.pushState({}, "", `${window.location.pathname}`);
    USERAXIOS.get(
      `/vacancies?page=${currentPage}` +
        (searchText ? `&title=${searchText}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching vacancy profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching vacancy profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("id") ||
      urlParams.get("company_name") ||
      urlParams.get("country_id") ||
      urlParams.get("city_id") ||
      urlParams.get("job_activity_id")
    );
  };

  const handleFiltersApply = (filters: VacancyFiltersType) => {
    setLoading(true);
    const { id, company_name, country_id, city_id, job_activity_id } = filters;
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("id");
    urlParams.delete("company_name");
    urlParams.delete("country_id");
    urlParams.delete("city_id");
    urlParams.delete("job_activity_id");
    if (id) urlParams.set("id", id);
    if (company_name) urlParams.set("company_name", company_name);
    if (country_id) urlParams.set("country_id", country_id);
    if (city_id) urlParams.set("city_id", city_id);
    if (job_activity_id) urlParams.set("job_activity_id", job_activity_id);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);
    USERAXIOS.get(
      `/vacancies?page=${currentPage}` +
        (id ? `&id=${id}` : "") +
        (company_name ? `&company_name=${company_name}` : "") +
        (country_id ? `&country_id=${country_id}` : "") +
        (city_id ? `&city_id=${city_id}` : "") +
        (job_activity_id ? `&job_activity_id=${job_activity_id}` : "") +
        (searchText ? `&title=${searchText}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching vacancy profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching vacancy profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await USERAXIOS.delete(`/vacancies/${vacancyDeleteId}`);
      setResult((prevResults) =>
        prevResults.filter((item) => item?.id !== vacancyDeleteId)
      );
      queryClient.invalidateQueries({ queryKey: ["Vacancies"] });
      handleClose();
      openSnackbar({
        open: true,
        message: "vacancy profile deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);
    } catch (error) {
      console.error("Error deleting vacancy profile:", error);
      openSnackbar({
        open: true,
        message: "Failed to delete vacancy profile",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    setActivateLoading(true);
    try {
      if (activateTarget?.status === "ACTIVE") {
        await USERAXIOS.post(`/vacancies/deactivate/${activateTarget?.id}`);
      } else {
        await USERAXIOS.post(`/vacancies/activate/${activateTarget?.id}`);
      }
      setResult((prevResults: Vacancy[]) =>
        prevResults.map((item) =>
          item.id === activateTarget?.id
            ? {
                ...item,
                status: item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
              }
            : item
        )
      );
      queryClient.invalidateQueries({ queryKey: ["Vacancies"] });
      setActivateModal(false);
      setActivateTarget(null);
      openSnackbar({
        open: true,
        message: `Vacancy ${
          activateTarget.status === "ACTIVE" ? "deactivated" : "activated"
        } successfully`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "success" },
      } as SnackbarProps);
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to update vacancy status",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      } as SnackbarProps);
    } finally {
      setActivateLoading(false);
    }
  };

  const handleAddEnglishVacancy = () => {
    window.location.href = `/vacancies/add-vacancy`;
  };

  const handleAddArabicVacancy = () => {
    window.location.href = `/vacancies/add-vacancy`;
    onChangeDirection(ThemeDirection.RTL);
    onChangeLocalization("ar");
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              {hasActiveFilters() && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={clearFilters}
                  sx={{ padding: ".3rem 2rem " }}
                >
                  <FormattedMessage id="Clear Filters" />
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<FilterOutlined />}
                onClick={() => setOpenFilters(true)}
                sx={{
                  padding: ".3rem 2rem",
                  gap: ".5rem",
                }}
              >
                <FormattedMessage id="Filters" />
              </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<PlusOutlined />}
                onClick={handleAddEnglishVacancy}
                sx={{
                  padding: ".3rem 2rem",
                  gap: ".5rem",
                  bgcolor: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                <FormattedMessage id="Add English Vacancy" />
              </Button>
              <Button
                variant="contained"
                startIcon={<PlusOutlined />}
                onClick={handleAddArabicVacancy}
                sx={{
                  padding: ".3rem 2rem",
                  gap: ".5rem",
                  bgcolor: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                <FormattedMessage id="Add Arabic Vacancy" />
              </Button>
            </Box>
          </Box>

          <ReusableTable
            data={result}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearch}
            searchedtext={tempSearchText}
            setSearchedtext={setTempSearchText}
            modalToggler={undefined} // Remove the old modal toggler since we have separate buttons
            renderDetailPanel={({ row }: any) => <></>}
          />
        </>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          handleClose={handleFiltersClose}
          onApplyFilters={handleFiltersApply}
          onClearFilters={clearFilters}
        />
      )}

      <ReusableDeleteDialog
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <ReusableActivateDialog
        open={activateModal}
        handleClose={() => setActivateModal(false)}
        handleActivate={handleActivate}
        title={
          activateTarget?.title ||
          activateTarget?.company?.user_name ||
          String(activateTarget?.id) ||
          "Vacancy"
        }
        isActivating={activateLoading}
        isActive={activateTarget?.status === "ACTIVE"}
      />
    </>
  );
};

export default VacanciesList;
